import { inject, Injectable, Injector } from '@angular/core';
import { ConfigurationService, EventTypes, OidcSecurityService, PublicEventsService } from 'angular-auth-oidc-client';
import { firstValueFrom, Observable } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';

import { AUTH_CONFIG_IDS, AuthConfigId } from '../../auth/auth.config';
import { SessionStore } from '../../core/services/session.store';

import { NavigationService } from './NavigationService';

@Injectable()
export class SecurityService {
    private readonly injector = inject(Injector);

    constructor(
        private oidcSecurityService: OidcSecurityService,
        private configurationService: ConfigurationService,
        private navigationService: NavigationService,
        private eventsSource: PublicEventsService,
    ) {}

    isAuthenticated(configId: AuthConfigId): Observable<boolean> {
        return this.eventsSource.registerForEvents().pipe(
            filter(
                (event) =>
                    event.type === EventTypes.CheckingAuthFinished ||
                    event.type === EventTypes.CheckingAuthFinishedWithError,
            ),
            switchMap(() => this.oidcSecurityService.isAuthenticated(configId)),
            take(1),
        );
    }

    async login(configId: AuthConfigId): Promise<void> {
        const loginRedirectUrl = location.href.replace('/unauthorized', '');
        const encodedLoginRedirectUrl = btoa(loginRedirectUrl);

        const activeConfigId = await this.getCurrentSession();

        await firstValueFrom(this.oidcSecurityService.setState(encodedLoginRedirectUrl, configId));

        if (activeConfigId && activeConfigId !== configId) {
            this.logout(activeConfigId, loginRedirectUrl);
            return;
        }

        this.oidcSecurityService.authorize(configId);
    }

    logout(configId: AuthConfigId, redirectUrl?: string): void {
        this.configurationService.getOpenIDConfiguration(configId).subscribe((authConfig) => {
            if (!authConfig?.authWellknownEndpoints) {
                console.error(`Cannot log out of "${configId}": its OpenID configuration is not loaded.`);
                return;
            }

            const clientId = authConfig.clientId;
            const postLogoutRedirectUri = encodeURIComponent(redirectUrl ?? authConfig.postLogoutRedirectUri ?? '');

            const logoutUrl = `${authConfig.authWellknownEndpoints.endSessionEndpoint}?client_id=${clientId}&post_logout_redirect_uri=${postLogoutRedirectUri}`;

            // Resolved here: SessionStore → UserService → HttpService → SecurityService is a cycle.
            this.injector.get(SessionStore).clear();
            this.oidcSecurityService.logoffLocal(configId);
            this.navigationService.goTo(logoutUrl);
        });
    }

    getAuthConfigId(): AuthConfigId {
        const pathname = window.location.pathname;
        if (pathname.startsWith('/enduser') || pathname.startsWith('/query')) {
            return AUTH_CONFIG_IDS.OWNER;
        }
        return AUTH_CONFIG_IDS.INSTALLER;
    }

    async getCurrentSession(): Promise<AuthConfigId | null> {
        for (const configId of Object.values(AUTH_CONFIG_IDS)) {
            const isAuthenticated = await firstValueFrom(this.oidcSecurityService.isAuthenticated(configId));

            if (isAuthenticated) {
                return configId;
            }
        }

        return null;
    }
}
